import { useEffect, useRef, useContext } from 'react';
import LayersContext from '../components/Helper/Layer/LayersContext';
import { canUsePassiveEvents, isNode, getOwnerDocument } from '../dom';
import { useEventCallback } from './useEventCallback';
const options = canUsePassiveEvents ? {
  passive: true
} : false;
export function useInteractAway(ref, handler) {
  const onInteractAway = useEventCallback(handler);
  const layers = useContext(LayersContext);
  const isNestedEvent = useRef(false);
  const isPointerDown = useRef(false);
  const hasPointerDragged = useRef(false);
  const pointerCoords = useRef({
    x: 0,
    y: 0
  });
  const ignoreEmulatedMouseEvents = useRef(false);
  useEffect(() => {
    function isValidEvent(evt) {
      if (typeof evt.button === 'number' && evt.button > 0) {
        return false;
      }
      if (!isNode(evt.target)) {
        return false;
      }
      const {
        documentElement
      } = getOwnerDocument(evt.target);
      if (!documentElement || !documentElement.contains(evt.target)) {
        return false;
      }
      if (layers?.messages?.contains(evt.target)) {
        return false;
      }
      if (isNestedEvent.current) {
        return false;
      }
      return ref.current && !ref.current.contains(evt.target);
    }
    function handleMouseDown(evt) {
      if (ignoreEmulatedMouseEvents.current) return;
      if (isValidEvent(evt)) {
        isPointerDown.current = true;
        hasPointerDragged.current = false;
        pointerCoords.current = {
          x: evt.pageX,
          y: evt.pageY
        };
      }
    }
    function handleTouchStart(evt) {
      if (isValidEvent(evt)) {
        const touch = evt.targetTouches[0];
        isPointerDown.current = true;
        hasPointerDragged.current = false;
        pointerCoords.current = {
          x: touch.pageX,
          y: touch.pageY
        };
      }
    }
    function handleMouseUp(evt) {
      if (ignoreEmulatedMouseEvents.current) {
        ignoreEmulatedMouseEvents.current = false;
        return;
      }
      if (isPointerDown.current && isValidEvent(evt)) {
        hasPointerDragged.current = hasDragged(pointerCoords.current, {
          x: evt.pageX,
          y: evt.pageY
        });
        if (!hasPointerDragged.current) {
          onInteractAway(evt);
        }
      }
      isPointerDown.current = false;
      isNestedEvent.current = false;
    }
    function handleTouchEnd(evt) {
      ignoreEmulatedMouseEvents.current = true;
      if (isPointerDown.current && isValidEvent(evt)) {
        const touch = evt.changedTouches[0];
        hasPointerDragged.current = hasDragged(pointerCoords.current, {
          x: touch.pageX,
          y: touch.pageY
        });
        if (!hasPointerDragged.current) {
          onInteractAway(evt);
        }
      }
      isPointerDown.current = false;
      isNestedEvent.current = false;
    }
    document.addEventListener('mousedown', handleMouseDown, options);
    document.addEventListener('mouseup', handleMouseUp, options);
    document.addEventListener('touchstart', handleTouchStart, options);
    document.addEventListener('touchend', handleTouchEnd, options);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, options);
      document.removeEventListener('mouseup', handleMouseUp, options);
      document.removeEventListener('touchstart', handleTouchStart, options);
      document.removeEventListener('touchend', handleTouchEnd, options);
    };
  }, [onInteractAway, layers, ref]);
  function markEventAsNested() {
    if (ignoreEmulatedMouseEvents.current) return;
    isNestedEvent.current = true;
  }
  return {
    onMouseDownCapture: markEventAsNested,
    onTouchStartCapture: markEventAsNested
  };
}
function hasDragged(prev, next, threshold = 3) {
  return Math.abs(prev.x - next.x) > threshold || Math.abs(prev.y - next.y) > threshold;
}